import React, { useState } from 'react';
import { StyleSheet, View, TextInput, Text, ActivityIndicator } from 'react-native';
import Navigation from '../../../navigation';
import { __WEB__, COLOR, FONT } from '../../../constants/styles';
import CTAButton from '../../components/cta_button';
import axios from '../../../axios-retried';
import { BASE_URL, env } from '../../../constants/env';

const BulkCallModal = (props) => {
  const [phoneNumbers, setPhoneNumbers] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handlePhoneNumbersChange = (text) => {
    setPhoneNumbers(text);
    setError(null);
    setSuccess(false);
  };

  const handleCancel = () => {
    Navigation.close(props);
  };

  const handleSave = async () => {
    // Validate input
    if (!phoneNumbers.trim()) {
      setError('Please enter at least one phone number');
      return;
    }

    if (!props.employerId) {
      setError('Employer ID is missing. Please try again.');
      return;
    }

    // Parse comma-separated phone numbers
    const phoneNumberList = phoneNumbers
      .split(',')
      .map((num) => num.trim())
      .filter((num) => num.length > 0);

    if (phoneNumberList.length === 0 || phoneNumberList.length % 3 !== 0) {
      setError(
        'Please enter valid phone numbers with first name and last name for each (3 values per contact, comma separated).'
      );
      return;
    }

    const phoneContacts = phoneNumberList.reduce(function (acc, cell, index) {
      const label = index % 3 === 0 ? 'phoneNumber' : index % 3 === 1 ? 'firstName' : 'lastName';
      const currentContactIndex = Math.floor(index / 3);

      if (!acc[currentContactIndex]) {
        acc[currentContactIndex] = { phoneNumber: '', firstName: '', lastName: '' };
      }

      acc[currentContactIndex][label] = cell;
      return acc;
    }, []);

    setIsLoading(true);
    setError(null);

    try {
      await axios.post(env(BASE_URL) + 'agent/do-call', {
        phones: phoneContacts,
        employerId: props.employerId,
      });

      setIsLoading(false);
      setSuccess(true);

      // Close modal after a brief success message
      setTimeout(() => {
        Navigation.close(props);
        props.onSuccess?.();
      }, 1500);
    } catch (err) {
      setIsLoading(false);
      setError(err.response?.data?.message || 'Failed to initiate calls. Please try again.');
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <Text style={styles.label}>Enter Phone Numbers (comma separated):</Text>
        <TextInput
          style={styles.textInput}
          value={phoneNumbers}
          onChangeText={handlePhoneNumbersChange}
          placeholder="e.g. 555-333-2222, John, Smith, 555-444-1111, Jane, Doe"
          multiline={true}
          numberOfLines={4}
          editable={!isLoading && !success}
          testID="phone-numbers-input"
        />

        {error && <Text style={styles.errorText}>{error}</Text>}
        {success && <Text style={styles.successText}>Calls initiated successfully!</Text>}

        <View style={styles.buttonContainer}>
          <CTAButton
            style={styles.cancelButton}
            onPress={handleCancel}
            label="Cancel"
            disabled={isLoading || success}
            testID="cancel-button"
          />
          <CTAButton
            style={styles.saveButton}
            onPress={handleSave}
            label={isLoading ? 'Sending...' : 'Send Calls'}
            disabled={isLoading || success}
            testID="save-button"
          />
        </View>

        {isLoading && (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={COLOR.emphasis} />
          </View>
        )}
      </View>
    </View>
  );
};

export default BulkCallModal;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'white',
  },
  content: {
    backgroundColor: 'white',
    borderRadius: __WEB__ ? 10 : 0,
    padding: 20,
    width: __WEB__ ? 500 : '100%',
    maxWidth: '90%',
  },
  label: {
    ...FONT.medium,
    fontSize: 16,
    marginBottom: 10,
    color: COLOR.text,
  },
  textInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 5,
    padding: 10,
    fontSize: 14,
    minHeight: 100,
    textAlignVertical: 'top',
    ...FONT.regular,
    marginBottom: 15,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 10,
  },
  cancelButton: {
    flex: 1,
    marginRight: 10,
    backgroundColor: '#999',
  },
  saveButton: {
    flex: 1,
    marginLeft: 10,
  },
  errorText: {
    color: 'red',
    fontSize: 14,
    marginBottom: 10,
    ...FONT.regular,
  },
  successText: {
    color: 'green',
    fontSize: 14,
    marginBottom: 10,
    ...FONT.medium,
  },
  loadingContainer: {
    marginTop: 15,
    alignItems: 'center',
  },
});
